import React, { FunctionComponent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InputField } from 'plume-ui';
import useFormControls from '../../../hooks/useFormControls';
import OutreachConfigSection from '../../../OutreachConfigSection';
import {
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  SubscriberClearMessageFormFields,
  SubscriberContentFormFields,
} from '../../../../../types';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  outreachTestMessageModalAtom,
  selectedCtaUrl,
} from '../../../../../../../state';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import { OutreachTypes } from '../../../../../../../urls';
import useValidation from '../../../hooks/useValidation';
import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';
import { Permission } from '../../../../../../../userPermissionConfig';
import {
  getMaxLengthFactory,
  getRemainingCharactersFactory,
} from '../../../helpers';

export type FormikSubscriberOutreachClearMessageValue = {
  subject: string;
  message: string;
};

const FormikSubscriberOutreachClearMessageField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSubscriberOutreachClearMessageValue
>> = ({ field, form }) => {
  const { t } = useTranslation();
  const { isSectionComplete } = useValidation();
  const { setPropertyValue } = useFormControls<
    FormikSubscriberOutreachClearMessageValue
  >(field, form);
  const setOutreachTestMessageModal = useSetRecoilState(
    outreachTestMessageModalAtom,
  );
  const { hasPermission } = useFeaturePermission();
  const canEditSubscriberPush = hasPermission(
    Permission.EditSubscriberPushOutreach,
  );
  const currentCtaUrl = useRecoilValue(selectedCtaUrl);
  const titleCopyId =
    'outreach.form.subscriberOutreachClearMessageSection.title';
  const subtitleCopyId =
    'outreach.form.subscriberOutreachClearMessageSection.subtitle';

  const onClickTestNotification = () => {
    setOutreachTestMessageModal({
      isOpen: true,
      messageType: 'clear',
      outreachType: OutreachTypes.Push,
      ctaUrl: currentCtaUrl,
    });
  };
  const isComplete = isSectionComplete(
    OutreachConfigSections.SubscriberOutreachClearMessage,
  );

  const getRemainingCharactersFor = getRemainingCharactersFactory(field);
  const getMaxLengthFor = getMaxLengthFactory(field);

  const remainingSubjectLength = getRemainingCharactersFor(
    SubscriberContentFormFields.Subject,
  );
  const remainingMessageLength = getRemainingCharactersFor(
    SubscriberContentFormFields.Message,
  );

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSubscriberPush}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__outreach-content-row OutreachContainer__outreach-content-row--horizontal-form">
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.subscriberOutreachClearMessageSection.subject" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SubscriberClearMessageFormFields.Subject}
                type="text"
                maxLength={getMaxLengthFor(
                  SubscriberClearMessageFormFields.Subject,
                )}
                value={field.value[SubscriberClearMessageFormFields.Subject]}
                onInput={(e) =>
                  setPropertyValue(
                    SubscriberClearMessageFormFields.Subject,
                    e.currentTarget.value,
                  )
                }
              />
              <div className="OutreachContainer__input-footer">
                <FormattedMessage
                  id="charactersRemaining"
                  params={{
                    characters: remainingSubjectLength,
                  }}
                />
              </div>
              <OutreachConfigFieldMessages
                propertyName={SubscriberClearMessageFormFields.Subject}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.subscriberOutreachClearMessageSection.message" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SubscriberClearMessageFormFields.Message}
                type="text"
                value={field.value[SubscriberClearMessageFormFields.Message]}
                onInput={(e) =>
                  setPropertyValue(
                    SubscriberClearMessageFormFields.Message,
                    e.currentTarget.value,
                  )
                }
              />
              <div className="OutreachContainer__input-footer">
                <FormattedMessage
                  id="charactersRemaining"
                  params={{
                    characters: remainingMessageLength,
                  }}
                />
              </div>
              <OutreachConfigFieldMessages
                propertyName={SubscriberClearMessageFormFields.Message}
                field={field}
                form={form}
              />
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.subscriberOutreachClearMessageSection.test" />
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              <Button
                styleVariant="quaternary"
                onClick={() => onClickTestNotification()}
              >
                <FormattedMessage id="outreach.form.subscriberOutreachClearMessageSection.testNotification" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSubscriberOutreachClearMessageField);
